import React, { FC, useState } from 'react';
import { styled } from '@mui/material/styles';
import { StyledLink, ListLabel } from '../Components';
import { List, ListItem, ListItemText, Button, Typography, Alert, Stack, CircularProgress, ToggleButton } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { formatDateOnly } from '../Formatters';
import { useMutation } from '@apollo/client';
import { Loop, CalendarMonth, Search } from '@mui/icons-material';
import { revisionTypeToDisplayText } from '../revisions/RevisionModel';
import { GetGuideQuery, GuideStatus } from '../__generated__/graphql';
import { SET_REVISION_ASSESMENT_IN_PROGRESS, SET_REVISION_PLANNED, START_REVISION } from './guide.graphql';
import { StyledPaper } from '../theme';

const PREFIX = 'Revision';

const classes = {
  paper: `${PREFIX}-paper`,
  detailItem: `${PREFIX}-detailItem`,
};

const StyledList = styled(List)(() => ({
  [`& .${classes.paper}`]: {
    minHeight: 260,
  },

  [`& .${classes.detailItem}`]: {
    paddingTop: 2,
    paddingBottom: 2,
    alignItems: 'flex-start',
  },
}));

export type Guide = NonNullable<GetGuideQuery['guide']>;

export const GuideRevision: FC<{ guide: Guide }> = ({ guide }) => {
  const guideId = guide.id;
  const guideName = guide.docName;
  const isNew = guide.status === GuideStatus.Planned;
  const isExpired = guide.status === GuideStatus.Expired;
  return (
    <StyledPaper className={classes.paper}>
      <Typography variant="h5" component="h3">
        {isNew ? 'Utarbeidelse' : 'Revisjon'}
      </Typography>

      {!guide.ongoingRevision && !isExpired && <CreateRevision guide={guide} />}
      {guide.ongoingRevision && <Details revision={guide.ongoingRevision} isNew={isNew} />}

      {!isNew && (
        <div style={{ marginLeft: 10 }}>
          <StyledLink to={`/revision?searchTerm=${guideName}&completed=0`}>Historikk</StyledLink>
        </div>
      )}
    </StyledPaper>
  );
};

const CreateRevision: FC<{ guide: Guide }> = ({ guide }) => {
  const guideId = guide.id;
  const revisionId = uuid();
  const [startRevision, { called: inProgress }] = useMutation(START_REVISION, { variables: { input: { id: guideId, revisionId } } });
  const [setRevisionPlanned] = useMutation(SET_REVISION_PLANNED);
  const [setRevisionAssesmentInProgress] = useMutation(SET_REVISION_ASSESMENT_IN_PROGRESS);
  const [isAlreadyStarted, setIsAlreadyStarted] = useState(false);

  const onClick = async () => {
    const result = await startRevision();
    if (result.data?.guideStartRevision.errors?.some((e) => e.code === 'OngoingRevisionExistsError')) {
      setIsAlreadyStarted(true);
    }
  };
  if (isAlreadyStarted) {
    return <Alert severity="error">Det finnes allerede en pågående revisjon for denne veilederen.</Alert>;
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, mb: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
      <ToggleButton
        value="planlagt"
        size="small"
        selected={guide.isRevisionPlanned}
        onChange={() => setRevisionPlanned({ variables: { input: { id: guideId, isRevisionPlanned: !guide.isRevisionPlanned } } })}>
        <CalendarMonth fontSize="small" sx={{ mr: 0.75 }} />
        Planlagt
      </ToggleButton>
      <ToggleButton
        value="kartlegging"
        size="small"
        selected={guide.isRevisionAssesmentInProgress}
        onChange={() =>
          setRevisionAssesmentInProgress({
            variables: { input: { id: guideId, isRevisionAssesmentInProgress: !guide.isRevisionAssesmentInProgress } },
          })
        }>
        <Search fontSize="small" sx={{ mr: 0.75 }} />
        Kartlegging pågår
      </ToggleButton>
      <Button disabled={inProgress} onClick={onClick} variant="outlined" startIcon={inProgress ? <CircularProgress size={20} /> : <Loop />}>
        Ny revisjon
      </Button>
    </Stack>
  );
};

type Revision = NonNullable<Guide['ongoingRevision']>;

const Details: FC<{ revision: Revision; isNew: boolean }> = ({ revision, isNew }) => {
  if (revision.canceledAt || revision.completedAt) return null;

  return (
    <StyledList>
      <ListItem className={classes.detailItem}>
        <ListLabel>Type:</ListLabel>
        <ListItemText primary={revisionTypeToDisplayText(revision.type)} />
      </ListItem>
      <ListItem className={classes.detailItem}>
        <ListLabel>Planlagt ferdig:</ListLabel>
        <ListItemText primary={formatDateOnly(revision.scheduledEndDate)} />
      </ListItem>
      <ListItem className={classes.detailItem}>
        <ListLabel>Fase:</ListLabel>
        <ListItemText primary={GetMilstone(revision.checklist!)} />
      </ListItem>
      <ListItem className={classes.detailItem}>
        <ListItemText
          primary={<StyledLink to={`/revision/${revision.id}`}>{isNew ? 'Gå til sjekkliste for utarbeidelse' : 'Gå til revisjon'}</StyledLink>}
        />
      </ListItem>
    </StyledList>
  );
};

type Checklist = NonNullable<Revision['checklist']>;
function GetMilstone(list: Checklist): string {
  for (let index = 0; index < list.milestones!.length; index++) {
    const milestone = list.milestones![index];
    for (let i = 0; i < milestone!.tasks!.length; i++) {
      const task = milestone!.tasks![i];
      if (task!.completed) continue;

      return `${index + 1} - ${milestone!.name}`;
    }
  }

  return '';
}
